/**
 * OpenClaw SDK compatibility layer — dynamic import wrappers with caching.
 *
 * Each wrapper resolves a capability by trying an ordered list of
 * (specifier, symbol) candidates and caching the first that yields a function.
 * If none match, the wrapper returns a no-op result.
 *
 * WHY A LIST AND NOT ONE PATH. Until 2026-08-05 each wrapper imported a single
 * `openclaw/dist/plugin-sdk/...` path. OpenClaw 2026.7.1's `exports` map does
 * not expose `./dist/*`, so all three threw ERR_PACKAGE_PATH_NOT_EXPORTED and
 * cached `false` — every one of these capabilities was silently dead on the
 * version our own production agent runs. Verified against the installed 2026.7.1.
 *
 * The comment this file used to carry — "degrades gracefully on older OpenClaw
 * versions" — was the trap. The break came from a NEWER harness, and graceful
 * degradation made a real capability loss look identical to a harness that
 * never had the API. Same failure shape as #480 Lever 2's version detection.
 *
 * ORDER: newest specifier first, legacy `dist/` path last. Two miss modes must
 * both fall through — the module failing to import, AND the module importing
 * cleanly without the symbol (2026.7.1 renamed `requestHeartbeatNow` to
 * `requestHeartbeat`, so the module is present but the old name is not).
 */

import type { AgentEventPayload, TranscriptUpdatePayload } from "./openclaw-types.js";

/** One place a capability might live, in one harness generation. */
export interface SdkCandidate {
  specifier: string;
  symbol: string;
}

/** Injectable so resolution is testable — a hardcoded `import()` is not. */
export type SdkImporter = (specifier: string) => Promise<Record<string, any>>;

const defaultImporter: SdkImporter = (specifier) => import(/* @vite-ignore */ specifier);

/**
 * Walk the candidates in order and return the first function found.
 * Returns false when nothing matches. Never throws.
 */
export async function resolveSdkFn(
  candidates: SdkCandidate[],
  importer: SdkImporter = defaultImporter,
): Promise<((...args: any[]) => any) | false> {
  for (const { specifier, symbol } of candidates) {
    try {
      const mod = await importer(specifier);
      const found = mod?.[symbol] ?? mod?.default?.[symbol];
      // Must be callable. A same-named non-function export is a miss, not a hit.
      if (typeof found === "function") return found;
    } catch {
      // Blocked, absent, or a different install layout — try the next.
    }
  }
  return false;
}

/* ── Capability tables ──────────────────────────────────────────────────── */

/** Wake the agent proactively before an outbound send. */
export const HEARTBEAT_CANDIDATES: SdkCandidate[] = [
  // 2026.7.x — exported subpath, and the symbol lost its `Now` suffix.
  { specifier: "openclaw/plugin-sdk/heartbeat-runtime", symbol: "requestHeartbeat" },
  // <=2026.6.x — deep dist path, original name.
  { specifier: "openclaw/dist/plugin-sdk/infra/heartbeat-wake.js", symbol: "requestHeartbeatNow" },
];

/** Agent-level events (tool_use, reasoning_complete, error…) for trust telemetry. */
export const AGENT_EVENT_CANDIDATES: SdkCandidate[] = [
  { specifier: "openclaw/plugin-sdk/agent-harness", symbol: "onAgentEvent" },
  { specifier: "openclaw/dist/plugin-sdk/infra/agent-events.js", symbol: "onAgentEvent" },
];

/** Session transcript deltas for behavioural analysis. */
export const TRANSCRIPT_CANDIDATES: SdkCandidate[] = [
  {
    specifier: "openclaw/plugin-sdk/memory-core-host-engine-foundation",
    symbol: "onSessionTranscriptUpdate",
  },
  {
    specifier: "openclaw/dist/plugin-sdk/sessions/transcript-events.js",
    symbol: "onSessionTranscriptUpdate",
  },
];

/* ── Wrappers ───────────────────────────────────────────────────────────── */

let _heartbeatFn: ((opts?: { reason?: string }) => Promise<void>) | false | null = null;

/**
 * Request an immediate heartbeat wake from the OpenClaw gateway.
 * Returns true if the API was available and called, false otherwise. Never throws.
 */
export async function requestHeartbeatNow(opts?: { reason?: string }): Promise<boolean> {
  if (_heartbeatFn === null) {
    _heartbeatFn = (await resolveSdkFn(HEARTBEAT_CANDIDATES)) as
      | ((opts?: { reason?: string }) => Promise<void>)
      | false;
  }
  if (typeof _heartbeatFn === "function") {
    try {
      await _heartbeatFn(opts);
      return true;
    } catch {
      return false;
    }
  }
  return false;
}

type AgentEventCallback = (event: AgentEventPayload) => void;
let _agentEventFn: ((cb: AgentEventCallback) => (() => void)) | false | null = null;

/**
 * Subscribe to agent-level events.
 * Returns an unsubscribe function, or a no-op if the API is unavailable.
 */
export async function onAgentEvent(callback: AgentEventCallback): Promise<() => void> {
  if (_agentEventFn === null) {
    _agentEventFn = (await resolveSdkFn(AGENT_EVENT_CANDIDATES)) as
      | ((cb: AgentEventCallback) => (() => void))
      | false;
  }
  if (typeof _agentEventFn === "function") {
    try {
      return _agentEventFn(callback);
    } catch {
      return () => {};
    }
  }
  return () => {};
}

type TranscriptCallback = (update: TranscriptUpdatePayload) => void;
let _transcriptFn: ((cb: TranscriptCallback) => (() => void)) | false | null = null;

/**
 * Subscribe to session transcript updates.
 * Returns an unsubscribe function, or a no-op if the API is unavailable.
 */
export async function onSessionTranscriptUpdate(callback: TranscriptCallback): Promise<() => void> {
  if (_transcriptFn === null) {
    _transcriptFn = (await resolveSdkFn(TRANSCRIPT_CANDIDATES)) as
      | ((cb: TranscriptCallback) => (() => void))
      | false;
  }
  if (typeof _transcriptFn === "function") {
    try {
      return _transcriptFn(callback);
    } catch {
      return () => {};
    }
  }
  return () => {};
}

/** Test seam: clear cached resolutions between cases. */
export function _resetCompatCacheForTest(): void {
  _heartbeatFn = null;
  _agentEventFn = null;
  _transcriptFn = null;
}
